import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    const tok = localStorage.getItem('zunoid_access');
    if (!tok) { setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      setTenant(data.tenant);
      applyTheme(data.user?.theme || 'dark');
      // Best-effort Web Push (offline notifications)
      if (data.user?.role !== 'superadmin') {
        import('./push').then((m) => m.ensurePushSubscription()).catch(() => {});
      }
    } catch {
      localStorage.removeItem('zunoid_access');
      localStorage.removeItem('zunoid_refresh');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  const finishLogin = async (data) => {
    localStorage.setItem('zunoid_access', data.access_token);
    localStorage.setItem('zunoid_refresh', data.refresh_token);
    setUser(data.user);
    applyTheme(data.user?.theme || 'dark');
    if (data.user.role !== 'superadmin') {
      try {
        const me = await api.get('/auth/me');
        setTenant(me.data.tenant);
        setUser(me.data.user);
      } catch { /* ignore */ }
    }
    return data.user;
  };

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.requires_2fa) {
      return { requires_2fa: true, challenge_token: data.challenge_token };
    }
    return finishLogin(data);
  };

  const complete2fa = async (challenge_token, code) => {
    const { data } = await api.post('/auth/2fa/verify-login', { challenge_token, code });
    return finishLogin(data);
  };

  const logout = () => {
    localStorage.removeItem('zunoid_access');
    localStorage.removeItem('zunoid_refresh');
    setUser(null);
    setTenant(null);
  };

  const setTheme = async (theme) => {
    setUser((u) => (u ? { ...u, theme } : u));
    applyTheme(theme);
    try { await api.post('/auth/theme', { theme }); } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, tenant, loading, login, complete2fa, logout, setTheme, refresh: bootstrap }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  if (theme === 'light') html.classList.remove('dark');
  else html.classList.add('dark');
  try { localStorage.setItem('zunoid_theme', theme); } catch {}
}

// Boot theme before React mount for no-flash
if (typeof window !== 'undefined') {
  try {
    const t = localStorage.getItem('zunoid_theme') || 'dark';
    applyTheme(t);
  } catch {}
}
